<template>
  <div class="pb-8">
    <base-dialog-delete-confirmation
        v-model="removeCommentDialog"
        prompt="Do you really want to remove this comment?"
        @removed="removeComment()"
      ></base-dialog-delete-confirmation>

    <div class="max-width-800 mx-auto mb-10">
      <v-textarea
          v-model="newComment"
          label="New Comment"
          counter
          variant="outlined"
          auto-grow
          rows="4"
          class="mt-1"
      ></v-textarea>
      <v-btn
          color="blue"
          theme="dark"
          class="button-xs-100"
          @click="AddComment()"
      >Add New Comment
      </v-btn>
    </div>

    <div
        v-if="!commentsTotal && !loading"
        class="ma-4"
    >
      No comments found.
    </div>
    
    <v-timeline
        v-if="commentsTotal"
        density="compact"
        side="end"
        align="center"
        class="comments px-4"
    >
      <v-timeline-item
          v-for="comment in comments"
          :key="comment.id"
          size="large"
          side="end"
      >
        <template v-slot:icon>
          <v-avatar
            :color="comment.user_id ? 'primary' : 'info'"
          >
          <span
              class="text-white"
          >
            {{ comment.avatar }}
          </span>
          </v-avatar>
        </template>
        <div class="comment-card-wrap">
          <v-card
            class="elevation-2 comment-card"
            color="default-panel"
          >
          <v-card-actions
              v-if="comment.user_id === currentUserId"
              class="comment-card__actions float-sm-right mt-1"
          >
            <v-tooltip location="bottom"
            >
              <template #activator="{ props }">
                <v-btn
                    icon
                    elevation="1"
                    class="ma-1"
                    @click="editedComment(comment.id)" v-bind="props"
                    size="small"
                    width="28"
                    height="28"
                    rounded="0"
                >
                  <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit comment</span>
            </v-tooltip>
            <v-tooltip location="bottom"
            >
              <template #activator="{ props }">
                <v-btn
                    icon
                    elevation="1"
                    class="ma-1"
                    @click="confirmRemoveComment(comment.id)"
                    color="red" v-bind="props"
                    size="small"
                    width="28"
                    height="28"
                    rounded="0"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete comment</span>
            </v-tooltip>
          </v-card-actions>
          <v-card-title class="comment-card-title">
            <span class="mr-auto">{{ comment.user_name || comment.integration_name }}
              <span v-if="comment.integration_id" class="font-weight-regular text-subtitle-2 text-medium-emphasis">(integration)</span>
            </span>
            <span class="font-italic text-subtitle-2 text-medium-emphasis">{{ getRelativeTime(comment.created_at) }}</span>
            <span
                v-if="comment.created_at !== comment.updated_at"
                class="font-italic text-subtitle-2 text-medium-emphasis ml-sm-3">Edited: {{ getRelativeTime(comment.updated_at) }}</span>
          </v-card-title>
          <v-card-text>
            <div
                v-if="editedCommentId === comment.id"
                class="comment-edit"
            >
              <v-textarea
                  v-model="editedCommentText"
                  label="Edit Comment"
                  counter
                  variant="outlined"
                  auto-grow
                  rows="2"
                  class="comment-edit__field"
              ></v-textarea>
              <v-btn
                  class="mb-4 mr-4"
                  @click="cancelEditing()"
              >Cancel
              </v-btn>
              <v-btn
                  color="blue"
                  theme="dark"
                  class="mb-4"
                  @click="updateComment(comment.id, {'text': editedCommentText})"
              >Save
              </v-btn>
            </div>
            <div
                v-else
                :class="(($store.state.isDarkMode) ? 'white--text' : 'black--text')"
                class="text-break"
            >
              {{ comment.text }}
            </div>
          </v-card-text>
          </v-card>
        </div>
      </v-timeline-item>
    </v-timeline>

    <div
        v-if="loading"
        class="ma-4"
    >
      <v-progress-linear
        indeterminate
        color="primary"
      ></v-progress-linear>
    </div>

    <div
        v-intersect="infiniteScrolling"
    ></div>
  </div>
</template>

<script>
import {displayDate} from "@/helpers/DateDisplay";

export default {
  name: 'BaseComments',

  data: () => ({
    comments: [],
    commentsTotal: 0,
    page: 0,
    loading: false,
    newComment: '',
    editedCommentId: null,
    editedCommentText: '',
    currentUserId: null,
    removeCommentDialog: false,
    removeCommentId: null,
  }),

  created() {
    this.currentUserId = this.$store.state.user.id;
  },

  methods: {
    infiniteScrolling: function (/*entries, observer, isIntersecting*/) {
      setTimeout(() => {
        this.loadData();
      }, 500);
    },
    editedComment: function (commentId) {
      this.editedCommentId = commentId;
      let editedComment = this.comments.find(item => item.id === commentId);
      if (editedComment) {
        this.editedCommentText = editedComment.text;
      }
    },
    cancelEditing: function () {
      this.editedCommentId = null;
      this.editedCommentText = '';
    },
    confirmRemoveComment: function (commentId) {
      this.removeCommentDialog = true;
      this.removeCommentId = commentId;
    },
    getRelativeTime: function (date) {
      return displayDate(new Date(date));
    },
  }
};
</script>

<style scoped>
/* Master: name left, relative time right (V3 card-title is block + nowrap by default) */
.comment-card-title {
  display: flex !important;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
  white-space: normal !important;
  overflow: visible;
}

/*
 * Master floated edit/delete top-right. V3 v-card-actions is a full-width flex
 * row without float utilities — keep actions out of the card flow.
 */
.comment-card {
  position: relative;
}

.comment-card__actions {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  min-height: unset !important;
  padding: 0 !important;
  width: auto !important;
  float: none !important;
}

.comment-card .comment-card-title {
  padding-right: 88px;
}

.comment-edit {
  width: 100%;
  clear: both;
}

.comment-edit__field {
  width: 100%;
}

.comment-edit__field :deep(.v-input),
.comment-edit__field :deep(.v-field) {
  width: 100%;
}
</style>
